import type { TmTask, TmListEntity, MeFeedItem } from "../api/tm";
import type { TaskRowVM } from "../components/TaskRow";
import type { Priority, Status } from "./design";
import { isoToDayKey, isOverdue } from "./date";

const STATUSES = new Set([
  "todo",
  "in_progress",
  "blocked",
  "done",
  "cancelled",
  "archived",
]);
const PRIOS = new Set(["low", "medium", "high", "urgent"]);

function asStatus(s: string): Status {
  return (STATUSES.has(s) ? s : "todo") as Status;
}

function asPriority(p?: string): Priority {
  return (p && PRIOS.has(p) ? p : "medium") as Priority;
}

/** Ref courte affichable (le backend n'a pas de ref métier — dérivée de l'id). */
export function taskRef(id: string): string {
  return `T-${id.replace(/-/g, "").slice(0, 4).toUpperCase()}`;
}

export interface TaskVMExtras {
  subtaskCount?: number;
  driverName?: string;
  assignees?: { ref: string; name: string }[];
  projectName?: string;
}

export function taskToVM(
  t: TmTask,
  lists?: TmListEntity[],
  extras?: TaskVMExtras,
): TaskRowVM {
  return {
    id: t.id,
    title: t.title,
    ref: taskRef(t.id),
    description: t.description,
    status: asStatus(t.status),
    priority: asPriority(t.priority),
    dueKey: t.due_date ? isoToDayKey(t.due_date) : undefined,
    createdKey: t.created_at ? isoToDayKey(t.created_at) : undefined,
    listName: lists?.find((l) => l.id === t.list_id)?.name,
    tags: t.tags ?? [],
    driverName: extras?.driverName,
    subtaskCount: extras?.subtaskCount,
    assignees: extras?.assignees,
    projectName: extras?.projectName,
    replicated: false,
  };
}

/** Miroir répliqué du feed /me (source ≠ "native") — lecture seule. */
export function feedItemToVM(f: MeFeedItem): TaskRowVM {
  return {
    id: f.id,
    title: f.title,
    ref: taskRef(f.id),
    status: asStatus(f.status),
    priority: asPriority(f.priority),
    dueKey: f.due_date ? isoToDayKey(f.due_date) : undefined,
    listName: f.context_label,
    tags: [],
    replicated: f.source !== "native",
  };
}

export interface TaskGroups {
  overdue: TaskRowVM[];
  active: TaskRowVM[];
  done: TaskRowVM[];
}

/** Groupes du design DECO : En retard / Tâches actives / Terminées. */
export function groupTasks(tasks: TaskRowVM[]): TaskGroups {
  const overdue: TaskRowVM[] = [];
  const active: TaskRowVM[] = [];
  const done: TaskRowVM[] = [];
  for (const t of tasks) {
    if (t.status === "archived") continue; // masquée de toutes les vues de travail
    if (t.status === "done" || t.status === "cancelled") done.push(t);
    else if (t.dueKey && isOverdue(t.dueKey)) overdue.push(t);
    else active.push(t);
  }
  const byDue = (a: TaskRowVM, b: TaskRowVM) =>
    (a.dueKey ?? "9999").localeCompare(b.dueKey ?? "9999");
  overdue.sort(byDue);
  active.sort(byDue);
  return { overdue, active, done };
}

export function isOpenStatus(s: string): boolean {
  return s !== "done" && s !== "cancelled" && s !== "archived";
}
